import { useEffect, useMemo, useState } from "react";
import { api, apiError } from "@/lib/api";
import { formatINR } from "@/lib/constants";
import { toast } from "sonner";
import { Package, Plus, Trash2, ShoppingCart, X, Save, Edit3, Receipt } from "lucide-react";

const EMPTY_KIT = { name: "", sku: "", hsn: "", unit_price: 0, stock: null, active: true, category: "", description: "" };

export default function AdminKitSales() {
  const [tab, setTab] = useState("catalog"); // catalog | sell | history
  const [kits, setKits] = useState([]);
  const [history, setHistory] = useState([]);
  const [editKit, setEditKit] = useState(null);
  const [showSale, setShowSale] = useState(false);

  const load = async () => {
    try {
      const [k, h] = await Promise.all([api.get("/admin/kits"), api.get("/admin/kit-sales")]);
      setKits(k.data || []);
      setHistory(h.data || []);
    } catch (e) { toast.error(apiError(e)); }
  };
  useEffect(() => { load(); }, []);

  const saveKit = async (form) => {
    try {
      await api.post("/admin/kits", form);
      toast.success("Saved"); setEditKit(null); load();
    } catch (e) { toast.error(apiError(e)); }
  };
  const deleteKit = async (id, name) => {
    if (!window.confirm(`Delete "${name}"?`)) return;
    try { await api.delete(`/admin/kits/${id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div data-testid="admin-kit-sales" className="p-6 md:p-8 max-w-6xl mx-auto">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-widest brand-orange">// Merchandise counter</div>
          <div className="font-display text-3xl font-black uppercase mt-1 flex items-center gap-2">
            <ShoppingCart size={26} /> Kit Sales
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setEditKit({ ...EMPTY_KIT })}
            data-testid="new-kit-btn"
            className="border border-brand-blue text-brand-blue px-4 py-2.5 text-xs uppercase font-bold tracking-widest flex items-center gap-2 hover:bg-brand-blue hover:text-black"
          >
            <Package size={12} /> New Kit
          </button>
          <button
            onClick={() => setShowSale(true)}
            data-testid="new-sale-btn"
            className="bg-brand-orange text-black px-4 py-2.5 text-xs uppercase font-bold tracking-widest flex items-center gap-2 hover:bg-white"
          >
            <Receipt size={12} /> New Sale
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b border-white/10 mb-6">
        {["catalog", "history"].map((t) => (
          <button
            key={t}
            onClick={() => setTab(t)}
            data-testid={`kit-tab-${t}`}
            className={`px-5 py-3 text-xs uppercase font-mono tracking-widest transition ${tab === t ? "border-b-2 border-brand-orange text-white" : "text-white/50 hover:text-white"}`}
          >
            {t === "catalog" ? "Catalog" : "Sales History"}
          </button>
        ))}
      </div>

      {tab === "catalog" && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="kits-grid">
          {kits.length === 0 && (
            <div className="col-span-full text-center text-white/40 py-12 border border-dashed border-white/10">
              No kits yet — click &quot;New Kit&quot; to add one.
            </div>
          )}
          {kits.map((k) => (
            <div key={k.id} className="border border-white/12 bg-[#131A2B] p-4" data-testid={`kit-${k.sku || k.id}`}>
              <div className="flex justify-between items-start gap-3">
                <div>
                  <div className="font-display text-base font-black uppercase">{k.name}</div>
                  <div className="font-mono text-[10px] text-white/50 mt-1">
                    {k.sku && <>SKU: {k.sku} · </>}{k.hsn && <>HSN: {k.hsn} · </>}
                    {k.category || "misc"}
                  </div>
                </div>
                <div className="text-right">
                  <div className="font-display text-lg font-black brand-blue">{formatINR(k.unit_price)}</div>
                  <div className="text-[10px] font-mono text-white/40">stock: {k.stock ?? "∞"}</div>
                </div>
              </div>
              {k.description && <div className="text-xs text-white/50 mt-2">{k.description}</div>}
              <div className="flex gap-2 mt-3">
                <button onClick={() => setEditKit(k)}
                  data-testid={`edit-kit-${k.sku || k.id}`}
                  className="text-[10px] font-mono uppercase tracking-widest border border-white/20 px-3 py-1.5 hover:border-brand-blue hover:text-brand-blue inline-flex items-center gap-1"
                >
                  <Edit3 size={11} /> Edit
                </button>
                <button onClick={() => deleteKit(k.id, k.name)}
                  data-testid={`delete-kit-${k.sku || k.id}`}
                  className="text-[10px] font-mono uppercase tracking-widest border border-red-500/40 text-red-400 px-3 py-1.5 hover:bg-red-500/20 inline-flex items-center gap-1"
                >
                  <Trash2 size={11} /> Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {tab === "history" && (
        <div className="border border-white/12" data-testid="sales-history">
          {history.length === 0 && (
            <div className="text-center text-white/40 py-12">No sales yet.</div>
          )}
          <table className="w-full text-sm">
            <thead className="border-b border-white/10 text-[10px] font-mono uppercase tracking-widest text-white/50">
              <tr>
                <th className="text-left px-4 py-3">Invoice</th>
                <th className="text-left px-4 py-3">Customer</th>
                <th className="text-right px-4 py-3">Items</th>
                <th className="text-right px-4 py-3">Total</th>
                <th className="text-right px-4 py-3">When</th>
              </tr>
            </thead>
            <tbody>
              {history.map((h) => (
                <tr key={h.id} className="border-b border-white/6 hover:bg-white/5" data-testid={`sale-${h.invoice_number}`}>
                  <td className="px-4 py-3 font-mono text-xs brand-blue">{h.invoice_number}</td>
                  <td className="px-4 py-3">{h.customer_name || "—"}</td>
                  <td className="px-4 py-3 text-right font-mono">{(h.line_items || []).length}</td>
                  <td className="px-4 py-3 text-right font-mono font-bold">{formatINR(h.total)}</td>
                  <td className="px-4 py-3 text-right font-mono text-[10px] text-white/40">
                    {h.created_at ? new Date(h.created_at).toLocaleString() : ""}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {editKit && <KitEditModal kit={editKit} onSave={saveKit} onClose={() => setEditKit(null)} />}
      {showSale && <SaleModal kits={kits} onClose={() => setShowSale(false)} onDone={() => { setShowSale(false); load(); setTab("history"); }} />}
    </div>
  );
}

function KitEditModal({ kit, onSave, onClose }) {
  const [form, setForm] = useState({ ...kit });
  const submit = (e) => {
    e.preventDefault();
    onSave({
      ...form,
      unit_price: Number(form.unit_price || 0),
      stock: form.stock === "" || form.stock === null ? null : Number(form.stock),
    });
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4 overflow-y-auto" onClick={onClose}>
      <form onSubmit={submit} onClick={(e) => e.stopPropagation()}
        data-testid="kit-edit-modal"
        className="w-full max-w-xl bg-[#131A2B] border border-brand-blue/40">
        <div className="p-5 border-b border-white/10 flex justify-between items-center">
          <div className="font-display text-lg font-black uppercase">{form.id ? "Edit Kit" : "New Kit"}</div>
          <button type="button" onClick={onClose} className="text-white/50 hover:text-white"><X size={20} /></button>
        </div>
        <div className="p-5 grid grid-cols-2 gap-4">
          <Field label="Name" span={2}><input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="kit-name" className="w-full bg-black/40 border border-white/15 px-3 py-2 text-sm" /></Field>
          <Field label="SKU"><input value={form.sku || ""} onChange={(e) => setForm({ ...form, sku: e.target.value })} data-testid="kit-sku" className="w-full bg-black/40 border border-white/15 px-3 py-2 text-sm" placeholder="CB-L-A" /></Field>
          <Field label="HSN / SAC"><input value={form.hsn || ""} onChange={(e) => setForm({ ...form, hsn: e.target.value })} data-testid="kit-hsn" className="w-full bg-black/40 border border-white/15 px-3 py-2 text-sm" placeholder="95069100" /></Field>
          <Field label="Unit price (₹)"><input type="number" min="0" value={form.unit_price} onChange={(e) => setForm({ ...form, unit_price: e.target.value })} data-testid="kit-price" className="w-full bg-black/40 border border-white/15 px-3 py-2 text-sm" /></Field>
          <Field label="Stock (blank = untracked)"><input type="number" min="0" value={form.stock ?? ""} onChange={(e) => setForm({ ...form, stock: e.target.value === "" ? null : e.target.value })} data-testid="kit-stock" className="w-full bg-black/40 border border-white/15 px-3 py-2 text-sm" /></Field>
          <Field label="Category"><input value={form.category || ""} onChange={(e) => setForm({ ...form, category: e.target.value })} data-testid="kit-category" className="w-full bg-black/40 border border-white/15 px-3 py-2 text-sm" placeholder="cricket, badminton, apparel" /></Field>
          <Field label="Description" span={2}><textarea rows="2" value={form.description || ""} onChange={(e) => setForm({ ...form, description: e.target.value })} className="w-full bg-black/40 border border-white/15 px-3 py-2 text-sm" /></Field>
        </div>
        <div className="p-5 border-t border-white/10 flex gap-2">
          <button type="button" onClick={onClose} className="flex-1 border border-white/15 py-2.5 font-mono text-xs uppercase tracking-widest">Cancel</button>
          <button type="submit" data-testid="kit-save-btn" className="flex-1 bg-brand-blue text-white py-2.5 font-bold text-xs uppercase tracking-widest hover:glow-blue inline-flex items-center justify-center gap-1">
            <Save size={12} /> Save
          </button>
        </div>
      </form>
    </div>
  );
}

function SaleModal({ kits, onClose, onDone }) {
  const [items, setItems] = useState([]);
  const [customerName, setCustomerName] = useState("");
  const [customerPhone, setCustomerPhone] = useState("");
  const [paymentMethod, setPaymentMethod] = useState("cash");
  const [busy, setBusy] = useState(false);
  const [orgCfg, setOrgCfg] = useState({ gst_rate: 18, gst_inclusive: false, company_type: "company" });

  useEffect(() => {
    api.get("/public/ngo").then((r) => setOrgCfg((prev) => ({ ...prev, ...(r.data || {}) }))).catch(() => {});
  }, []);

  const addCatalog = (kit) => {
    const existing = items.findIndex((it) => it.kit_id === kit.id);
    if (existing >= 0) {
      patchItem(existing, "quantity", items[existing].quantity + 1);
    } else {
      setItems([...items, { kit_id: kit.id, name: kit.name, hsn: kit.hsn, quantity: 1, unit_price: kit.unit_price }]);
    }
  };
  const addAdhoc = () => setItems([...items, { kit_id: null, name: "", hsn: "", quantity: 1, unit_price: 0 }]);
  const removeItem = (i) => setItems(items.filter((_, j) => j !== i));
  const patchItem = (i, field, value) => {
    const next = [...items];
    next[i] = { ...next[i], [field]: value };
    setItems(next);
  };

  const priceSubtotal = useMemo(() => items.reduce((sum, it) => sum + Number(it.unit_price || 0) * Number(it.quantity || 0), 0), [items]);
  const gstRate = ((orgCfg.company_type || "company") === "company") ? Number(orgCfg.gst_rate ?? 18) : 0;
  const gstInclusive = !!orgCfg.gst_inclusive;
  const gstAmount = gstRate === 0
    ? 0
    : gstInclusive
      ? Math.round(priceSubtotal - priceSubtotal / (1 + gstRate / 100))
      : Math.round((priceSubtotal * gstRate) / 100);
  const baseExGst = gstInclusive ? priceSubtotal - gstAmount : priceSubtotal;
  const total = gstInclusive ? priceSubtotal : priceSubtotal + gstAmount;

  const submit = async () => {
    if (items.length === 0) { toast.error("Add at least one item"); return; }
    for (const [i, it] of items.entries()) {
      if (!it.name || !it.name.trim()) { toast.error(`Item #${i + 1} needs a name`); return; }
      if (Number(it.unit_price) <= 0) { toast.error(`Item #${i + 1} needs a price`); return; }
      if (Number(it.quantity) <= 0) { toast.error(`Item #${i + 1} needs a positive quantity`); return; }
    }
    setBusy(true);
    try {
      await api.post("/admin/kit-sales", {
        items: items.map((it) => ({
          ...it, quantity: Number(it.quantity), unit_price: Number(it.unit_price),
        })),
        customer_name: customerName || undefined,
        customer_phone: customerPhone || undefined,
        payment_method: paymentMethod,
      });
      toast.success(`Kit sale · ${formatINR(total)}`);
      onDone();
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4 overflow-y-auto" onClick={onClose}>
      <div className="w-full max-w-3xl bg-[#131A2B] border border-brand-orange/40" onClick={(e) => e.stopPropagation()} data-testid="kit-sale-modal">
        <div className="p-5 border-b border-white/10 flex justify-between items-center">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-widest brand-orange">// Merchandise counter</div>
            <div className="font-display text-lg font-black uppercase mt-0.5">New Kit Sale</div>
          </div>
          <button type="button" onClick={onClose} className="text-white/50 hover:text-white"><X size={20} /></button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-0">
          {/* Left: Catalog picker */}
          <div className="border-r border-white/10 p-4 md:col-span-1 max-h-96 overflow-y-auto">
            <div className="font-mono text-[10px] uppercase tracking-widest text-white/50 mb-2">Pick from catalog</div>
            {(kits || []).filter((k) => k.active !== false).map((k) => (
              <button key={k.id} type="button" onClick={() => addCatalog(k)}
                data-testid={`sale-add-${k.sku || k.id}`}
                className="w-full text-left border border-white/12 p-2 mb-2 hover:border-brand-orange hover:bg-brand-orange/5 text-xs">
                <div className="font-display text-[13px] font-black uppercase">{k.name}</div>
                <div className="font-mono text-[10px] text-white/50">{formatINR(k.unit_price)} · stock {k.stock ?? "∞"}</div>
              </button>
            ))}
            <button type="button" onClick={addAdhoc}
              data-testid="sale-add-adhoc"
              className="w-full mt-2 border border-dashed border-white/30 py-2 text-[11px] font-mono uppercase tracking-widest text-white/60 hover:text-white hover:border-white">
              + Add Ad-hoc Item
            </button>
          </div>

          {/* Right: Basket + summary */}
          <div className="md:col-span-2 p-4">
            <div className="font-mono text-[10px] uppercase tracking-widest text-white/50 mb-2">Basket</div>
            {items.length === 0 && (
              <div className="text-white/40 text-xs py-8 text-center border border-dashed border-white/10">
                Pick items from the catalog on the left, or add an ad-hoc row.
              </div>
            )}
            <div className="space-y-2 max-h-64 overflow-y-auto">
              {items.map((it, i) => (
                <div key={i} className="grid grid-cols-12 gap-2 items-center" data-testid={`sale-line-${i}`}>
                  <input className="col-span-5 bg-black/40 border border-white/15 px-2 py-1.5 text-xs" placeholder="Item name" value={it.name}
                    onChange={(e) => patchItem(i, "name", e.target.value)} disabled={!!it.kit_id} />
                  <input type="number" min="1" className="col-span-2 bg-black/40 border border-white/15 px-2 py-1.5 text-xs" value={it.quantity}
                    onChange={(e) => patchItem(i, "quantity", e.target.value)} />
                  <input type="number" min="0" className="col-span-3 bg-black/40 border border-white/15 px-2 py-1.5 text-xs" placeholder="₹ each" value={it.unit_price}
                    onChange={(e) => patchItem(i, "unit_price", e.target.value)} />
                  <div className="col-span-1 text-[10px] font-mono text-right">{formatINR(Number(it.quantity || 0) * Number(it.unit_price || 0))}</div>
                  <button type="button" onClick={() => removeItem(i)} className="col-span-1 text-red-400 hover:text-red-300"><Trash2 size={12} /></button>
                </div>
              ))}
            </div>

            <div className="mt-4 border-t border-white/10 pt-4 space-y-1 text-xs font-mono">
              {gstRate === 0 ? (
                <div className="flex justify-between text-white/60"><span>Subtotal</span><span>{formatINR(priceSubtotal)}</span></div>
              ) : gstInclusive ? (
                <>
                  <div className="flex justify-between text-white/60"><span>Price (incl. GST)</span><span>{formatINR(priceSubtotal)}</span></div>
                  <div className="flex justify-between text-white/50"><span>Base (ex-GST)</span><span>{formatINR(baseExGst)}</span></div>
                  <div className="flex justify-between text-white/50"><span>GST {gstRate}%</span><span>{formatINR(gstAmount)}</span></div>
                </>
              ) : (
                <>
                  <div className="flex justify-between text-white/60"><span>Subtotal</span><span>{formatINR(priceSubtotal)}</span></div>
                  <div className="flex justify-between text-white/60"><span>GST {gstRate}%</span><span>{formatINR(gstAmount)}</span></div>
                </>
              )}
              <div className="flex justify-between text-base font-display font-black brand-blue"><span>Total</span><span data-testid="sale-total">{formatINR(total)}</span></div>
            </div>

            <div className="grid grid-cols-2 gap-2 mt-4">
              <input placeholder="Customer name (optional)" data-testid="sale-customer-name" className="bg-black/40 border border-white/15 px-3 py-2 text-xs" value={customerName} onChange={(e) => setCustomerName(e.target.value)} />
              <input placeholder="Phone" data-testid="sale-customer-phone" className="bg-black/40 border border-white/15 px-3 py-2 text-xs" value={customerPhone} onChange={(e) => setCustomerPhone(e.target.value)} />
              <select value={paymentMethod} onChange={(e) => setPaymentMethod(e.target.value)}
                data-testid="sale-payment-method"
                className="bg-black/40 border border-white/15 px-3 py-2 text-xs uppercase tracking-widest col-span-2">
                <option value="cash">Cash (at desk)</option>
                <option value="upi">UPI</option>
                <option value="card">Card</option>
                <option value="cheque">Cheque</option>
                <option value="bank_transfer">Bank transfer</option>
              </select>
            </div>

            <div className="mt-4 flex gap-2">
              <button type="button" onClick={onClose} className="flex-1 border border-white/15 py-2.5 font-mono text-xs uppercase tracking-widest">Cancel</button>
              <button type="button" onClick={submit} disabled={busy || items.length === 0}
                data-testid="sale-confirm-btn"
                className="flex-1 bg-brand-orange text-black py-2.5 font-bold text-xs uppercase tracking-widest hover:bg-white disabled:opacity-50">
                {busy ? "Processing…" : `Complete Sale · ${formatINR(total)}`}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function Field({ label, span = 1, children }) {
  return (
    <div className={span === 2 ? "col-span-2" : ""}>
      <label className="block font-mono text-[10px] uppercase tracking-widest text-white/50 mb-1">{label}</label>
      {children}
    </div>
  );
}
